import { reportErrorToSentry } from "@aix/webutils/src/sentry"
import { Button, cn, MegaBytes, Separator } from "@aix/ui"
import { Download04Icon, TwitterIcon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { createFileRoute } from "@tanstack/react-router"
import { FacebookIcon, LinkedinIcon } from "lucide-react"
import { useEffect, useState } from "react"
import MarketingLayout from "~/layouts/MarketingLayout"
import { api } from "~/utils/api"
import { downloadFile } from "~/utils/download"
import { usePH } from "~/utils/posthogClient"
import { EVMAnalyticsEvent } from "@aix/vidmax-shared/src/analytics"

const PublicVideoPage = () => {
  const { linkId } = Route.useParams()
  const { capture } = usePH()
  const [error, setError] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const { data, isLoading } = api.video.validateShareableLink.useQuery(
    { linkId },
    {
      enabled: !!linkId,
      onError: (err) => {
        setError(err.message || "Failed to load video")
      },
    },
  )

  useEffect(() => {
    if (data && !data.valid) {
      setError(data.error || "Invalid link")
    }
  }, [data])

  // Handle direct download
  const handleDownload = async () => {
    if (!data?.video?.signedUrl) return

    setIsDownloading(true)
    const filename = data.video.title ? `${data.video.title}.mp4` : `video-${linkId}.mp4`

    capture(EVMAnalyticsEvent.VIDEO_DOWNLOADED, {
      videoId: data.video.id,
      $set_once: { first_download_at: new Date().toISOString() },
    })

    setTimeout(() => {
      setIsDownloading(false)
    }, 15000)

    try {
      const result = await downloadFile(data.video.signedUrl, filename)
      setIsDownloading(false)
    } catch (error) {
      void reportErrorToSentry(error, "linkIdPage.handleDownload")
      setIsDownloading(false)
    }
  }

  // Handle social sharing
  const handleSocialShare = (platform: string) => {
    const currentUrl = window.location.href
    const encodedUrl = encodeURIComponent(currentUrl)
    const encodedText = encodeURIComponent(`Check out this video: ${data?.video?.title || ""}`)

    let url = ""
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
        break
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
        break
      case "linkedin":
        url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}%20${encodedUrl}`
        break
    }

    if (url) {
      window.open(url, "_blank", "width=600,height=400")
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  if (error || (data && !data.valid)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-white mb-2">Video Not Available</h1>
          <p className="text-gray-400 mb-6">{error || "This link has expired or is invalid."}</p>
          <Button
            variant="default"
            size="lg"
            onClick={() => window.open("https://vidmax.ai", "_blank")}
            className="w-full"
          >
            Create Your Own Videos with Vidmax
          </Button>
          <p className="text-gray-500 text-sm mt-3">Join thousands creating amazing videos with AI</p>
        </div>
      </div>
    )
  }

  if (!data?.video) {
    return null
  }

  const aspectRatio = data.video.aspectRatio || "9:16"
  const [widthStr, heightStr] = aspectRatio.split(":")
  const width = Number.parseInt(widthStr || "9", 10)
  const height = Number.parseInt(heightStr || "16", 10)

  return (
    <>
      <MarketingLayout>
        {/* <Head>
          <title>{data.video.title || "Shared Video"} - Vidmax</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta property="og:title" content={data.video.title || "Shared Video"} />
          <meta property="og:type" content="video.other" />
          <meta property="og:video" content={data.video.signedUrl} />
          <meta property="og:video:type" content="video/mp4" />
          {data.video.duration && (
            <meta property="og:video:duration" content={Math.round(data.video.duration).toString()} />
          )}
        </Head> */}

        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
          <div className="w-full max-w-5xl">
            <div className="flex flex-col items-center justify-center w-full gap-6 md:items-start md:flex-row">
              {/* Video Player */}
              <div
                className={cn("relative bg-black rounded-lg overflow-hidden shadow-2xl", "w-full", {
                  "max-w-[300px]": aspectRatio === "9:16",
                  "max-w-[400px]": aspectRatio === "1:1" || aspectRatio === "4:3",
                  "max-w-[500px]": aspectRatio === "16:9",
                })}
                style={{ aspectRatio: width / height }}
              >
                <video className="w-full h-full" src={data.video.signedUrl} controls autoPlay={false} playsInline>
                  <track kind="captions" />
                </video>
              </div>

              {/* Controls Panel */}
              <div className="flex flex-col gap-7 w-[300px]">
                <h1 className="text-xl font-semibold text-white">{data.video.title || "Shared Video"}</h1>

                <Separator className="bg-gray-700" />

                <div className="flex flex-col gap-2">
                  {/* Download Button */}
                  <Button
                    variant="default"
                    size="lg"
                    className="w-full gap-2"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    loading={isDownloading}
                    leftIcon={<HugeiconsIcon icon={Download04Icon} size={18} color="#000000" />}
                  >
                    <span>Download</span>
                    {data.video.fileSizeInBytes && <MegaBytes sizeInBytes={data.video.fileSizeInBytes} />}
                  </Button>

                  <Separator className="bg-gray-700 mt-2" />

                  {/* Social Sharing */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-400">Share on Social Media</h3>
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => handleSocialShare("twitter")}
                        className="bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500 transition-colors"
                      >
                        <HugeiconsIcon icon={TwitterIcon} size={16} />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => handleSocialShare("facebook")}
                        className="bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500 transition-colors"
                      >
                        <FacebookIcon size={16} />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => handleSocialShare("linkedin")}
                        className="bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500 transition-colors"
                      >
                        <LinkedinIcon size={16} />
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="bg-gray-700" />

                {/* Video Info */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex px-4 py-3 text-sm font-medium bg-gray-800 rounded">
                    <span className="flex-1 text-gray-400">Duration:</span>
                    <span className="text-white">
                      {data.video.duration ? `${Math.floor(data.video.duration)}s` : "Unknown"}
                    </span>
                  </div>

                  {data.video.fileSizeInBytes && (
                    <div className="flex px-4 py-3 text-sm font-medium bg-gray-800 rounded">
                      <span className="flex-1 text-gray-400">Size:</span>
                      <span className="text-white">
                        <MegaBytes sizeInBytes={data.video.fileSizeInBytes} />
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="text-center pt-4">
                  <p className="text-gray-400 text-sm">
                    Created with{" "}
                    <a
                      href="https://vidmax.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Vidmax.ai
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MarketingLayout>
    </>
  )
}

export const Route = createFileRoute("/v/$linkId")({
  component: PublicVideoPage,
})
